/**
 * The pages of the shell.
 *
 * A design opens in the schematic workspace (M1); everything else is the M0
 * shell's plain pages, with no drawing surface.
 */

import { useCallback, useEffect, useId, useRef, useState, type DragEvent, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import { api, type Design, type DisplayUnitsBody, type Job, type LibraryEntity, type Program, type Project, type VersionSummary } from "./api/client";
import {
  Banner,
  ConfirmDialog,
  Empty,
  ErrorNotice,
  Loading,
  NOTICE_DISMISS_MS,
  ProgressBar,
  RelativeTime,
  RemoveButton,
  Timestamp,
} from "./components";
import { AddIcon, ArchiveIcon, CopyIcon, DeleteIcon, DisclosureIcon, MoveIcon, RestoreIcon } from "./icons";
import { DefinitionWorkspace } from "./workspace/DefinitionWorkspace";
import { DesignThumbnail } from "./workspace/DesignThumbnail";
import { DesignWorkspace } from "./workspace/DesignWorkspace";
import { StartFromDesignDialog, StartFromProjectDialog } from "./workspace/StartFromDesignDialog";
import { UnitsPicker } from "./workspace/UnitsPicker";
import { AccountSettings } from "./accounts";
import { GRID_SPACING, loadGrid, saveGrid } from "./workspace/preferences";

/** Load-on-mount with error and loading state, so pages do not each reinvent it. */
function useLoader<T>(load: () => Promise<T>, deps: unknown[]): {
  data: T | null;
  error: unknown;
  loading: boolean;
  reload: () => void;
} {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    loadRef
      .current()
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((caught: unknown) => {
        if (!cancelled) setError(caught);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  return { data, error, loading, reload: () => setNonce((n) => n + 1) };
}

// --- Permanent deletion (D33) ---------------------------------------------------

/** A pending deletion: what, and how to do it. The dialog asks before `run`. */
interface PendingDeletion {
  title: string;
  body: string;
  run: () => Promise<void>;
  /** Defaults to permanent deletion's wording. */
  confirmLabel?: string;
  footnote?: string;
  /**
   * A name the designer must type before confirming. For deletions that take
   * a lot with them, so a misclick on the wrong card cannot remove a project.
   */
  typeToConfirm?: string;
}

/**
 * The confirm-then-delete flow shared by the project and design lists. The
 * server is the authority on what may go (published work never does), so a
 * refusal is shown in the dialog rather than predicted here.
 */
function useDeletion(onDeleted: () => void) {
  const [pending, setPending] = useState<PendingDeletion | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [typed, setTyped] = useState("");
  const ask = (deletion: PendingDeletion) => {
    setError(null);
    setTyped("");
    setPending(deletion);
  };
  const expected = pending?.typeToConfirm;
  const confirm = async () => {
    if (!pending) return;
    setBusy(true);
    setError(null);
    try {
      await pending.run();
      setPending(null);
      onDeleted();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };
  const dialog = pending ? (
    <ConfirmDialog
      title={pending.title}
      confirmLabel={pending.confirmLabel ?? "Delete permanently"}
      busy={busy}
      error={error}
      focusField={expected !== undefined}
      confirmDisabled={expected !== undefined && typed.trim() !== expected.trim()}
      onConfirm={() => void confirm()}
      onCancel={() => setPending(null)}
    >
      <p style={{ margin: 0 }}>{pending.body}</p>
      <p className="muted" style={{ margin: 0 }}>
        {pending.footnote ?? "This cannot be undone. Published work is never deleted."}
      </p>
      {expected !== undefined && (
        <label className="stack" style={{ gap: "0.35rem" }}>
          <span>
            Type <strong>{expected}</strong> to confirm
          </span>
          <input
            value={typed}
            autoComplete="off"
            spellCheck={false}
            onChange={(event) => setTyped(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && typed.trim() === expected.trim() && !busy) void confirm();
            }}
          />
        </label>
      )}
    </ConfirmDialog>
  ) : null;
  return { ask, dialog };
}

// --- Projects --------------------------------------------------------------

const plural = (count: number, one: string, many = `${one}s`) => `${count} ${count === 1 ? one : many}`;

const byLastEdit = (a: Project, b: Project) =>
  (b.last_edited_at ?? b.created_at).localeCompare(a.last_edited_at ?? a.created_at);

/** What the page asks the delete dialog, archive and restore to do with a card. */
interface CardActions {
  askDelete: (project: Project) => void;
  archive: (project: Project) => void;
  restore: (project: Project) => void;
  /** Start a new design from one of the project's designs: copy or fork. */
  askCopy: (project: Project) => void;
  /** Open the move dialog: the keyboard's way to do what dragging does. Absent with no programmes. */
  askMove?: (project: Project) => void;
  /** Dragging a card onto a programme (or out of one) moves it there. */
  dragStart?: (project: Project) => void;
  dragEnd?: () => void;
}

/** The type a dragged card carries, so other drags (files, text) are ignored. */
const PROJECT_DRAG = "application/x-fluidea-project";

/**
 * Programmes and projects on one page. Each programme is a section holding
 * its projects as cards; projects in no programme follow, then, folded away,
 * the archived ones (D38). A project inside a programme the caller is not a
 * member of is listed under "Other programmes", so it never shows another
 * programme's identifier or goes missing.
 */
export function ProjectsPage() {
  const projects = useLoader(() => api.listProjects(), []);
  const programmes = useLoader(() => api.listPrograms().catch(() => [] as Program[]), []);
  const [creatingProject, setCreatingProject] = useState<{ programId: string | null } | null>(null);
  const [creatingProgramme, setCreatingProgramme] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [archivedNonce, setArchivedNonce] = useState(0);
  const [folded, setFolded] = useState<ReadonlySet<string>>(new Set());
  const [actionError, setActionError] = useState<unknown>(null);
  const [notice, setNotice] = useState<{ text: string; undo?: () => void } | null>(null);
  const [moving, setMoving] = useState<Project | null>(null);
  const [copying, setCopying] = useState<Project | null>(null);
  const navigate = useNavigate();
  const [dragging, setDragging] = useState<Project | null>(null);

  const reloadAll = () => {
    projects.reload();
    setArchivedNonce((n) => n + 1);
  };
  const deletion = useDeletion(reloadAll);

  const act = async (run: () => Promise<unknown>, text: string, undo?: () => void) => {
    setActionError(null);
    try {
      await run();
      setNotice({ text, undo });
      reloadAll();
    } catch (caught) {
      setActionError(caught);
    }
  };
  const actions: CardActions = {
    askDelete: (project) =>
      deletion.ask({
        title: `Delete ${project.name}?`,
        body: `The project, ${plural(project.design_count ?? 0, "design")} in it and its project library entities will be removed from the database. Only possible if none of it was ever published.`,
        run: () => api.deleteProject(project.id),
        typeToConfirm: project.name,
      }),
    archive: (project) =>
      void act(
        () => api.archiveProject(project.id),
        `Archived ${project.name}.`,
        () => void act(() => api.restoreProject(project.id), `Restored ${project.name}.`),
      ),
    restore: (project) => void act(() => api.restoreProject(project.id), `Restored ${project.name}.`),
    askCopy: setCopying,
  };

  const visibleProgrammes = [...(programmes.data ?? [])].sort((a, b) => a.name.localeCompare(b.name));
  const programmeName = (id: string | null | undefined) =>
    id ? (visibleProgrammes.find((programme) => programme.id === id)?.name ?? "its programme") : null;

  /** Move, then say where to, with an undo that puts it back where it was. */
  const move = (project: Project, programId: string | null) => {
    if ((project.program_id ?? null) === programId) return;
    const from = project.program_id ?? null;
    const where = (id: string | null) => (id ? `into ${programmeName(id)}` : "out of its programme");
    void act(
      () => api.moveProject(project.id, programId),
      `Moved ${project.name} ${where(programId)}.`,
      () => void act(() => api.moveProject(project.id, from), `Moved ${project.name} back.`),
    );
  };
  if (visibleProgrammes.length > 0) {
    actions.askMove = setMoving;
    actions.dragStart = setDragging;
    actions.dragEnd = () => setDragging(null);
  }
  /** Where a card may be dropped: anywhere it is not already. */
  const dropInto = (programId: string | null): DropTarget | undefined =>
    dragging && (dragging.program_id ?? null) !== programId
      ? {
          label: programId ? `Move into ${programmeName(programId)}` : "Move out of its programme",
          onDrop: () => {
            const project = dragging;
            setDragging(null);
            move(project, programId);
          },
        }
      : undefined;
  const known = new Set(visibleProgrammes.map((programme) => programme.id));
  const all = [...(projects.data ?? [])].sort(byLastEdit);
  const inProgramme = (id: string) => all.filter((project) => project.program_id === id);
  const elsewhere = all.filter((project) => project.program_id && !known.has(project.program_id));
  const loose = all.filter((project) => !project.program_id);
  const grouped = visibleProgrammes.length > 0 || elsewhere.length > 0;
  const toggle = (key: string) =>
    setFolded((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <div className="stack">
      <div className="page-header">
        <h2>Projects</h2>
        <div className="row">
          <button type="button" className="with-icon" onClick={() => setCreatingProgramme(true)}>
            <AddIcon size={16} />
            New programme
          </button>
          <button type="button" className="primary with-icon" onClick={() => setCreatingProject({ programId: null })}>
            <AddIcon size={16} />
            New project
          </button>
        </div>
      </div>
      <ErrorNotice error={projects.error ?? actionError} />
      {notice && (
        <Banner className="notice" autoDismissMs={NOTICE_DISMISS_MS} resetKey={notice.text} onDismiss={() => setNotice(null)}>
          {notice.text}
          {notice.undo && (
            <>
              {" "}
              <button
                type="button"
                className="link-button"
                onClick={() => {
                  const undo = notice.undo;
                  setNotice(null);
                  undo?.();
                }}
              >
                Undo
              </button>
            </>
          )}
        </Banner>
      )}

      {projects.loading ? (
        <Loading what="projects" />
      ) : all.length === 0 && visibleProgrammes.length === 0 ? (
        <Empty>No projects yet.</Empty>
      ) : (
        <>
          {visibleProgrammes.map((programme) => {
            const members = inProgramme(programme.id);
            return (
              <FoldingSection
                key={programme.id}
                title={programme.name}
                count={members.length}
                description={programme.description}
                folded={folded.has(programme.id)}
                onToggle={() => toggle(programme.id)}
                drop={dropInto(programme.id)}
                action={
                  <button
                    type="button"
                    className="icon-button neutral"
                    aria-label={`New project in ${programme.name}`}
                    title={`New project in ${programme.name}`}
                    onClick={() => setCreatingProject({ programId: programme.id })}
                  >
                    <AddIcon size={16} />
                  </button>
                }
              >
                {members.length === 0 ? (
                  <Empty>No projects in this programme yet.</Empty>
                ) : (
                  <ProjectGrid label={`Projects in ${programme.name}`} projects={members} actions={actions} />
                )}
              </FoldingSection>
            );
          })}
          {elsewhere.length > 0 && (
            <FoldingSection
              title="Other programmes"
              count={elsewhere.length}
              description="Projects you are a member of, inside programmes you are not."
              folded={folded.has("elsewhere")}
              onToggle={() => toggle("elsewhere")}
            >
              <ProjectGrid label="Projects in other programmes" projects={elsewhere} actions={actions} />
            </FoldingSection>
          )}
          {grouped ? (
            // Shown while dragging even when empty, so a project can be taken out of every programme.
            (loose.length > 0 || dropInto(null)) && (
              <FoldingSection
                title="Not in a programme"
                count={loose.length}
                folded={folded.has("loose")}
                onToggle={() => toggle("loose")}
                drop={dropInto(null)}
              >
                {loose.length === 0 ? (
                  <Empty>Drop a project here to take it out of its programme.</Empty>
                ) : (
                  <ProjectGrid label="Projects not in a programme" projects={loose} actions={actions} />
                )}
              </FoldingSection>
            )
          ) : (
            <ProjectGrid label="Projects" projects={loose} actions={actions} />
          )}
        </>
      )}

      {!projects.loading && (
        <FoldingSection
          title="Archived"
          folded={!showArchived}
          onToggle={() => setShowArchived((shown) => !shown)}
          quiet
        >
          <ArchivedProjects nonce={archivedNonce} actions={actions} programmes={visibleProgrammes} />
        </FoldingSection>
      )}

      {creatingProject && (
        <CreateProjectDialog
          programmes={visibleProgrammes}
          programId={creatingProject.programId}
          onCreated={() => {
            setCreatingProject(null);
            projects.reload();
          }}
          onCancel={() => setCreatingProject(null)}
        />
      )}
      {copying && (
        <StartFromProjectDialog
          projectId={copying.id}
          leadDesignId={copying.lead_design_id}
          onCreated={(created) => navigate(`/designs/${created.id}`)}
          onCancel={() => setCopying(null)}
        />
      )}
      {moving && (
        <MoveProjectDialog
          project={moving}
          programmes={visibleProgrammes}
          onMove={(programId) => {
            setMoving(null);
            move(moving, programId);
          }}
          onCancel={() => setMoving(null)}
        />
      )}
      {creatingProgramme && (
        <CreateProgrammeDialog
          onCreated={() => {
            setCreatingProgramme(false);
            programmes.reload();
          }}
          onCancel={() => setCreatingProgramme(false)}
        />
      )}
      {deletion.dialog}
    </div>
  );
}

/** A section a dragged card can be dropped on, and what dropping does. */
interface DropTarget {
  /** Said by the section while a card is over it. */
  label: string;
  onDrop: () => void;
}

/**
 * A titled section whose body folds shut. The heading's button carries the
 * state. With `drop`, the whole section, folded or not, takes a dragged card.
 */
function FoldingSection({
  title,
  count,
  description,
  folded,
  onToggle,
  action,
  quiet = false,
  drop,
  children,
}: {
  title: string;
  count?: number;
  description?: string;
  folded: boolean;
  onToggle: () => void;
  action?: ReactNode;
  /** Muted, for what is kept out of the way (archived projects). */
  quiet?: boolean;
  drop?: DropTarget;
  children: ReactNode;
}) {
  const bodyId = useId();
  const [over, setOver] = useState(false);
  // A drag that ends elsewhere, or is no longer droppable here, leaves no highlight behind.
  useEffect(() => {
    if (!drop) setOver(false);
  }, [drop]);
  const dropping = drop
    ? {
        onDragOver: (event: DragEvent) => {
          if (!event.dataTransfer.types.includes(PROJECT_DRAG)) return;
          event.preventDefault();
          event.dataTransfer.dropEffect = "move";
          setOver(true);
        },
        onDragLeave: (event: DragEvent) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOver(false);
        },
        onDrop: (event: DragEvent) => {
          event.preventDefault();
          setOver(false);
          drop.onDrop();
        },
      }
    : {};
  const classes = ["fold-section", quiet && "quiet", drop && "droppable", over && "drop-over"].filter(Boolean).join(" ");
  return (
    <section className={classes} aria-label={title} {...dropping}>
      {over && drop && (
        <span className="drop-hint" role="status">
          {drop.label}
        </span>
      )}
      <div className="fold-header">
        <h3>
          <button type="button" className="fold-toggle" aria-expanded={!folded} aria-controls={bodyId} onClick={onToggle}>
            <span className={`fold-caret${folded ? " folded" : ""}`}>
              <DisclosureIcon size={14} />
            </span>
            {title}
            {count !== undefined && <span className="fold-count">{count}</span>}
          </button>
        </h3>
        {action}
      </div>
      {!folded && (
        <div id={bodyId} className="stack fold-body">
          {description && (
            <p className="muted" style={{ margin: 0 }}>
              {description}
            </p>
          )}
          {children}
        </div>
      )}
    </section>
  );
}

/** Loaded only once the Archived section is opened, and again after any change. */
function ArchivedProjects({ nonce, actions, programmes }: { nonce: number; actions: CardActions; programmes: readonly Program[] }) {
  const archived = useLoader(() => api.listArchivedProjects(), [nonce]);
  const names = new Map(programmes.map((programme) => [programme.id, programme.name]));
  if (archived.loading) return <Loading what="archived projects" />;
  if (archived.error) return <ErrorNotice error={archived.error} />;
  const list = [...(archived.data ?? [])].sort(byLastEdit);
  if (list.length === 0) return <Empty>Nothing archived. Archiving takes a project out of the list without removing anything.</Empty>;
  return <ProjectGrid label="Archived projects" projects={list} actions={actions} archived programmeNames={names} />;
}

function ProjectGrid({
  label,
  projects,
  actions,
  archived = false,
  programmeNames,
}: {
  label: string;
  projects: readonly Project[];
  actions: CardActions;
  archived?: boolean;
  /** Shown on each card when the grid is not already a programme's section. */
  programmeNames?: ReadonlyMap<string, string>;
}) {
  return (
    <ul className="project-grid" aria-label={label}>
      {projects.map((project) => (
        <ProjectCard
          key={project.id}
          project={project}
          actions={actions}
          archived={archived}
          programme={project.program_id ? programmeNames?.get(project.program_id) : undefined}
        />
      ))}
    </ul>
  );
}

function ProjectCard({
  project,
  actions,
  archived,
  programme,
}: {
  project: Project;
  actions: CardActions;
  archived: boolean;
  programme?: string;
}) {
  // Released design versions are what the card reports. Deletion is decided
  // by all published work, project blocks included (D33).
  const released = project.published_design_version_count ?? 0;
  const published = project.published_version_count ?? 0;
  const keptReason =
    released > 0
      ? "Published work is kept permanently"
      : "Its project blocks have published versions, which are kept permanently";
  return (
    <li
      className={`project-card${archived ? " archived" : ""}${actions.dragStart && !archived ? " draggable" : ""}`}
      draggable={!archived && actions.dragStart !== undefined}
      onDragStart={(event) => {
        if (archived || !actions.dragStart) return;
        // Firefox starts no drag without data; the type also marks it as a card.
        event.dataTransfer.setData(PROJECT_DRAG, project.id);
        event.dataTransfer.effectAllowed = "move";
        actions.dragStart(project);
      }}
      onDragEnd={() => actions.dragEnd?.()}
    >
      <div className="project-thumbnail">
        <DesignThumbnail designId={project.lead_design_id} />
      </div>
      <div className="project-card-body">
        {/* Not draggable itself: the link covers the card, and the card is what moves. */}
        <Link className="project-card-link" to={`/projects/${project.id}`} draggable={false}>
          {project.name}
        </Link>
        <div className="project-card-meta">
          {archived && project.archived_at ? (
            <>
              Archived <RelativeTime value={project.archived_at} />
            </>
          ) : project.last_edited_at ? (
            <>
              Edited <RelativeTime value={project.last_edited_at} />
            </>
          ) : (
            <>
              Created <RelativeTime value={project.created_at} />
            </>
          )}
        </div>
        <div className="project-card-meta">
          {programme && <span className="project-card-programme">{programme}</span>}
          <span>{plural(project.design_count ?? 0, "design")}</span>
          {released > 0 && <span>{plural(released, "published version")}</span>}
        </div>
      </div>
      <div className="project-card-actions">
        {archived ? (
          <button
            type="button"
            className="icon-button neutral"
            aria-label={`Restore project ${project.name}`}
            title={`Restore ${project.name} to the list`}
            onClick={() => actions.restore(project)}
          >
            <RestoreIcon size={16} />
          </button>
        ) : (
          <>
            <button
              type="button"
              className="icon-button neutral"
              aria-label={`Copy or fork a design in ${project.name}`}
              title={
                (project.design_count ?? 0) === 0
                  ? `${project.name} has no design to copy`
                  : `Start a new design from one in ${project.name}: copy its draft, or fork a published version`
              }
              disabled={(project.design_count ?? 0) === 0}
              onClick={() => actions.askCopy(project)}
            >
              <CopyIcon size={16} />
            </button>
            {actions.askMove && (
              <button
                type="button"
                className="icon-button neutral"
                aria-label={`Move project ${project.name} to a programme`}
                title={`Move ${project.name} to a programme (or drag it onto one)`}
                onClick={() => actions.askMove?.(project)}
              >
                <MoveIcon size={16} />
              </button>
            )}
          <button
            type="button"
            className="icon-button neutral"
            aria-label={`Archive project ${project.name}`}
            title={`Archive ${project.name}: out of the list, nothing removed`}
            onClick={() => actions.archive(project)}
          >
            <ArchiveIcon size={16} />
          </button>
          </>
        )}
        <button
          type="button"
          className="icon-button"
          aria-label={`Delete project ${project.name}`}
          title={published > 0 ? keptReason : `Delete project ${project.name}`}
          disabled={published > 0}
          onClick={() => actions.askDelete(project)}
        >
          <DeleteIcon size={16} />
        </button>
      </div>
    </li>
  );
}

/** The keyboard's way to move a project, and the way to see where it would go. */
function MoveProjectDialog({
  project,
  programmes,
  onMove,
  onCancel,
}: {
  project: Project;
  programmes: readonly Program[];
  onMove: (programId: string | null) => void;
  onCancel: () => void;
}) {
  const current = project.program_id ?? "";
  const [programme, setProgramme] = useState(current);
  const field = useId();
  return (
    <ConfirmDialog
      title={`Move ${project.name}`}
      confirmLabel="Move"
      danger={false}
      focusField
      confirmDisabled={programme === current}
      onConfirm={() => onMove(programme || null)}
      onCancel={onCancel}
    >
      <div className="stack" style={{ gap: "0.35rem" }}>
        <label htmlFor={field}>Programme</label>
        <select id={field} value={programme} aria-describedby={`${field}-help`} onChange={(event) => setProgramme(event.target.value)}>
          <option value="">Not in a programme</option>
          {programmes.map((entry) => (
            <option key={entry.id} value={entry.id}>
              {entry.name}
            </option>
          ))}
        </select>
        <span id={`${field}-help`} className="muted field-help">
          Members of the programme it leaves lose the access they had through it; members of the one it joins gain it.
        </span>
      </div>
    </ConfirmDialog>
  );
}

function CreateProjectDialog({
  programmes,
  programId,
  onCreated,
  onCancel,
}: {
  programmes: readonly Program[];
  programId: string | null;
  onCreated: () => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState("");
  const [programme, setProgramme] = useState(programId ?? "");
  const programmeField = useId();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const create = async () => {
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await api.createProject(name.trim(), programme || undefined);
      onCreated();
    } catch (caught) {
      setError(caught);
      setBusy(false);
    }
  };
  return (
    <ConfirmDialog
      title="New project"
      confirmLabel="Create project"
      busyLabel="Creating…"
      danger={false}
      busy={busy}
      focusField
      confirmDisabled={!name.trim()}
      error={error}
      onConfirm={() => void create()}
      onCancel={onCancel}
    >
      <label className="stack" style={{ gap: "0.35rem" }}>
        Name
        <input
          value={name}
          maxLength={200}
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => event.key === "Enter" && void create()}
        />
      </label>
      {programmes.length > 0 && (
        <div className="stack" style={{ gap: "0.35rem" }}>
          <label htmlFor={programmeField}>Programme</label>
          <select
            id={programmeField}
            value={programme}
            aria-describedby={`${programmeField}-help`}
            onChange={(event) => setProgramme(event.target.value)}
          >
            <option value="">Not in a programme</option>
            {programmes.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.name}
              </option>
            ))}
          </select>
          <span id={`${programmeField}-help`} className="muted field-help">
            Members of the programme get the same access to the project.
          </span>
        </div>
      )}
    </ConfirmDialog>
  );
}

function CreateProgrammeDialog({ onCreated, onCancel }: { onCreated: () => void; onCancel: () => void }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const create = async () => {
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await api.createProgram(name.trim());
      onCreated();
    } catch (caught) {
      setError(caught);
      setBusy(false);
    }
  };
  return (
    <ConfirmDialog
      title="New programme"
      confirmLabel="Create programme"
      busyLabel="Creating…"
      danger={false}
      busy={busy}
      focusField
      confirmDisabled={!name.trim()}
      error={error}
      onConfirm={() => void create()}
      onCancel={onCancel}
    >
      <p className="muted" style={{ margin: 0 }}>
        A client engagement or product family. Its members get the same access to every project inside it.
      </p>
      <label className="stack" style={{ gap: "0.35rem" }}>
        Name
        <input
          value={name}
          maxLength={200}
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => event.key === "Enter" && void create()}
        />
      </label>
    </ConfirmDialog>
  );
}

export function ProjectPage() {
  const { projectId = "" } = useParams();
  const project = useLoader(() => api.getProject(projectId), [projectId]);
  const designs = useLoader(() => api.listDesigns(projectId), [projectId]);
  const [name, setName] = useState("");
  const [kind, setKind] = useState<Design["kind"]>("microfluidic");
  const [submitError, setSubmitError] = useState<unknown>(null);
  const deletion = useDeletion(designs.reload);
  const [startFrom, setStartFrom] = useState<Design | null>(null);
  const navigate = useNavigate();

  const create = async () => {
    setSubmitError(null);
    try {
      await api.createDesign(projectId, name.trim(), kind);
      setName("");
      designs.reload();
    } catch (caught) {
      setSubmitError(caught);
    }
  };

  return (
    <div className="stack">
      <h2>{project.data?.name ?? "Project"}</h2>
      <ErrorNotice error={project.error ?? designs.error ?? submitError} />

      <div className="card row">
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="New design name"
          aria-label="New design name"
        />
        {/* Decided once: it sets which parts the palette offers and which
            profiles can check the design (D34). */}
        <select value={kind} onChange={(event) => setKind(event.target.value as Design["kind"])} aria-label="Kind of design">
          <option value="microfluidic">Microfluidic device</option>
          <option value="tubing_set">Tubing set</option>
        </select>
        <button className="primary" onClick={create} disabled={!name.trim()}>
          Create design
        </button>
      </div>

      <p className="muted">
        Competing architectures live side by side here. Each one keeps its own version
        history.
      </p>
      {project.data && (
        <div className="card row">
          {/* D49: what this project's designs show volumes and flows in, unless a design says. */}
          <UnitsPicker
            value={project.data.display_units ?? null}
            inherited={null}
            inheritedFrom="By each design's kind (tubing set mL, microfluidic µL)"
            onChange={(next) => {
              setSubmitError(null);
              api
                .setProjectDisplayUnits(projectId, next as DisplayUnitsBody)
                .then(() => project.reload())
                .catch(setSubmitError);
            }}
          />
        </div>
      )}

      {designs.loading ? (
        <Loading what="designs" />
      ) : !designs.data || designs.data.length === 0 ? (
        <Empty>No designs yet.</Empty>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Kind</th>
              <th>Latest version</th>
              <th>Draft revision</th>
              <th>Forked from</th>
              <th>
                <span className="visually-hidden">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {designs.data.map((design: Design) => (
              <tr key={design.id}>
                <td>
                  <Link to={`/designs/${design.id}`}>{design.name}</Link>
                </td>
                <td className="muted">{design.kind === "tubing_set" ? "Tubing set" : "Microfluidic"}</td>
                <td>{design.latest_version_number ?? <span className="muted">none</span>}</td>
                <td>{design.draft_revision ?? "—"}</td>
                <td className="muted">
                  <ForkedFrom design={design} />
                </td>
                <td className="row-actions">
                  <button
                    type="button"
                    className="icon-button neutral"
                    aria-label={`Copy or fork design ${design.name}`}
                    title={`Start a new design from ${design.name}: copy its draft, or fork a published version`}
                    onClick={() => setStartFrom(design)}
                  >
                    <CopyIcon size={16} />
                  </button>
                  <RemoveButton
                    label={`Delete design ${design.name}`}
                    disabledReason={
                      design.latest_version_number !== null && design.latest_version_number !== undefined
                        ? "Published designs are kept permanently"
                        : undefined
                    }
                    onClick={() =>
                      deletion.ask({
                        title: `Delete ${design.name}?`,
                        body: `Its draft (revision ${design.draft_revision ?? "—"}) will be removed from the database.`,
                        run: () => api.deleteDesign(design.id),
                      })
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <BlockList
        title="Blocks in this project"
        load={() => api.listLibrary("blocks", projectId).then((all) => all.filter((b) => b.project_id === projectId))}
        deps={[projectId]}
        empty="No project blocks yet. Save a selection as a block from a design's menu."
        removable
      />
      {deletion.dialog}
      {startFrom && (
        <StartFromDesignDialog
          design={startFrom}
          onCreated={(created) => navigate(`/designs/${created.id}`)}
          onCancel={() => setStartFrom(null)}
        />
      )}
    </div>
  );
}

/** Where a fork branched from, by name and version when the reader can see it (D9). */
function ForkedFrom({ design }: { design: Design }) {
  if (!design.forked_from_design_id) return <>—</>;
  const version = design.forked_from_version_number;
  if (!design.forked_from_design_name) {
    return <span title="In a project you cannot open">another project's design</span>;
  }
  return (
    <>
      <Link to={`/designs/${design.forked_from_design_id}`}>{design.forked_from_design_name}</Link>
      {version !== null && version !== undefined && <> v{version}</>}
    </>
  );
}

// --- Blocks (D20) -----------------------------------------------------------

/** Block definitions with links to their own editing view (requirement 8.7). */
function BlockList({
  title,
  load,
  deps,
  empty,
  removable = false,
}: {
  title: string;
  load: () => Promise<LibraryEntity[]>;
  deps: unknown[];
  empty: string;
  /** Offer to remove a block no design uses: a project's own, not the shared library's. */
  removable?: boolean;
}) {
  const blocks = useLoader(load, deps);
  const removal = useDeletion(blocks.reload);
  return (
    <section className="stack" aria-label={title}>
      <h3>{title}</h3>
      <ErrorNotice error={blocks.error} />
      {blocks.loading ? (
        <Loading what="blocks" />
      ) : !blocks.data || blocks.data.length === 0 ? (
        <Empty>{empty}</Empty>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Latest version</th>
              <th>Created</th>
              {removable && (
                <th>
                  <span className="visually-hidden">Actions</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {blocks.data.map((block) => (
              <tr key={block.id}>
                <td>
                  <Link to={`/library/blocks/${block.id}`}>{block.name}</Link>
                </td>
                <td>{block.latest_version_number ?? <span className="muted">unpublished</span>}</td>
                <td>
                  <Timestamp value={block.created_at} />
                </td>
                {removable && (
                  <td className="row-actions">
                    <RemoveButton
                      label={`Remove block ${block.name}`}
                      onClick={() =>
                        removal.ask({
                          title: `Remove ${block.name}?`,
                          body: "It leaves this project's block list and palette. Only possible if no design's working draft has it placed; detached instances and published designs keep what they had.",
                          run: () => api.removeBlock(block.id),
                          confirmLabel: "Remove",
                          footnote: "Its published versions are kept, as all published work is.",
                        })
                      }
                    />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {removal.dialog}
    </section>
  );
}

/** The shared library's blocks. A project's own are listed on its page. */
export function LibraryPage() {
  return (
    <div className="stack">
      <h2>Library</h2>
      <p className="muted">
        Blocks shared by every project. Open one to edit it on its own; publishing a version offers each placed
        instance the update, and changes nothing until that update is accepted.
      </p>
      <BlockList
        title="Shared blocks"
        load={() => api.listLibrary("blocks").then((all) => all.filter((b) => b.is_library_scoped))}
        deps={[]}
        empty="No shared blocks yet. Save a selection as a block and choose the shared library."
      />
    </div>
  );
}

/** A block definition, opened independently of any design (requirement 8.7). */
export function BlockDefinitionPage() {
  const { entityId = "" } = useParams();
  const from = (useLocation().state ?? null) as { from?: string; fromName?: string } | null;
  const entity = useLoader(() => api.getLibraryEntity(entityId), [entityId]);
  const draft = useLoader(() => api.getLibraryDraft(entityId), [entityId]);

  if (entity.error || draft.error) {
    return (
      <div className="page-pad">
        <ErrorNotice error={entity.error ?? draft.error} />
      </div>
    );
  }
  if (!entity.data || !draft.data) {
    return (
      <div className="page-pad">
        <Loading what="block" />
      </div>
    );
  }
  if (entity.data.kind !== "blocks") {
    return (
      <div className="page-pad">
        <ErrorNotice error={new Error("That library entity is not a block.")} />
      </div>
    );
  }
  const back = from?.from
    ? { to: from.from, label: from.fromName ?? "Back to the design" }
    : entity.data.project_id
      ? { to: `/projects/${entity.data.project_id}`, label: "Project" }
      : { to: "/library", label: "Library" };
  return (
    <DefinitionWorkspace
      key={entityId}
      entity={entity.data}
      initialDraft={draft.data}
      back={back}
      onPublished={() => undefined}
    />
  );
}

// --- Designs ---------------------------------------------------------------

export function DesignPage() {
  const { designId = "" } = useParams();
  const design = useLoader(() => api.getDesign(designId), [designId]);
  const draft = useLoader(() => api.getDraft(designId), [designId]);
  const versions = useLoader(() => api.listVersions(designId), [designId]);

  const latest = (versions.data ?? []).reduce<VersionSummary | null>(
    (best, version) => (best === null || version.number > best.number ? version : best),
    null,
  );

  if (design.error || draft.error) {
    return (
      <div className="page-pad">
        <ErrorNotice error={design.error ?? draft.error} />
      </div>
    );
  }
  // The workspace is created once per design with the draft it opened on;
  // after that it owns the working document, so a reload of these loaders
  // (after publishing) must not remount it.
  if (!design.data || !draft.data || versions.loading && !versions.data) {
    return (
      <div className="page-pad">
        <Loading what="design" />
      </div>
    );
  }

  return (
    <DesignWorkspace
      key={designId}
      design={design.data}
      initialDraft={draft.data}
      latestVersion={latest ? { id: latest.id, number: latest.number } : null}
      onPublished={() => versions.reload()}
      subtitle={
        design.data.forked_from_version_id ? (
          <>
            forked from <ForkedFrom design={design.data} />
          </>
        ) : undefined
      }
      history={<VersionHistory designId={designId} versions={versions.data} loading={versions.loading} />}
    />
  );
}

function VersionHistory({
  designId,
  versions,
  loading,
}: {
  designId: string;
  versions: VersionSummary[] | null;
  loading: boolean;
}) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [diff, setDiff] = useState<Awaited<ReturnType<typeof api.diffVersions>> | null>(null);
  const [error, setError] = useState<unknown>(null);

  const compare = async () => {
    setError(null);
    try {
      setDiff(await api.diffVersions(designId, from, to));
    } catch (caught) {
      setError(caught);
    }
  };

  if (loading) return <Loading what="version history" />;
  if (!versions || versions.length === 0) {
    return <Empty>No published versions yet.</Empty>;
  }

  return (
    <div className="stack">
      <h3>Version history</h3>
      <table>
        <thead>
          <tr>
            <th>#</th>
            <th>Published</th>
            <th>Message</th>
            <th>Derived from</th>
          </tr>
        </thead>
        <tbody>
          {versions.map((version) => (
            <tr key={version.id}>
              <td>{version.number}</td>
              <td>
                <Timestamp value={version.created_at} />
              </td>
              <td>{version.message || <span className="muted">—</span>}</td>
              <td className="mono muted">
                {version.derived_from_version_id
                  ? version.derived_from_version_id.slice(0, 8)
                  : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="card row">
        <label>
          From{" "}
          <select value={from} onChange={(event) => setFrom(event.target.value)}>
            <option value="">choose</option>
            {versions.map((version) => (
              <option key={version.id} value={version.id}>
                {version.number}
              </option>
            ))}
          </select>
        </label>
        <label>
          To{" "}
          <select value={to} onChange={(event) => setTo(event.target.value)}>
            <option value="">choose</option>
            {versions.map((version) => (
              <option key={version.id} value={version.id}>
                {version.number}
              </option>
            ))}
          </select>
        </label>
        <button onClick={compare} disabled={!from || !to || from === to}>
          Compare
        </button>
      </div>

      <ErrorNotice error={error} />

      {diff && (
        <div className="card stack">
          <strong>
            {diff.entries.length === 0
              ? "No differences."
              : `${diff.entries.length} difference(s)`}
          </strong>
          {diff.touched_elements.length > 0 && (
            <div className="muted">Elements touched: {diff.touched_elements.join(", ")}</div>
          )}
          <ul>
            {diff.entries.map((entry, index) => (
              <li key={`${entry.location}-${index}`} className={`diff-${entry.change}`}>
                <code>{entry.location}</code> {entry.change}
                {entry.change === "changed" && (
                  <>
                    {": "}
                    <code>{JSON.stringify(entry.before)}</code> →{" "}
                    <code>{JSON.stringify(entry.after)}</code>
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// --- Activity --------------------------------------------------------------

export function ActivityPage() {
  const { data, error, loading } = useLoader(() => api.listActivity({ limit: 100 }), []);

  return (
    <div className="stack">
      <h2>Activity</h2>
      <p className="muted">Append-only. Scoped to what you have access to.</p>
      <ErrorNotice error={error} />

      {loading ? (
        <Loading what="activity" />
      ) : !data || data.length === 0 ? (
        <Empty>Nothing recorded yet.</Empty>
      ) : (
        <table>
          <thead>
            <tr>
              <th>When</th>
              <th>Action</th>
              <th>Entity</th>
              <th>Detail</th>
            </tr>
          </thead>
          <tbody>
            {data.map((entry) => (
              <tr key={entry.id}>
                <td>
                  <Timestamp value={entry.occurred_at} />
                </td>
                <td className="mono">{entry.action}</td>
                <td>{entry.entity_kind}</td>
                <td className="muted mono">
                  {Object.keys(entry.detail).length > 0 ? JSON.stringify(entry.detail) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// --- Jobs ------------------------------------------------------------------

export function JobsPage() {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [message, setMessage] = useState("hello");

  const refresh = useCallback(async () => {
    try {
      setJobs(await api.listJobs());
    } catch (caught) {
      setError(caught);
    }
  }, []);

  useEffect(() => {
    void refresh();
    // Polling rather than websockets: progress updates are the only live data in
    // M0, and a second transport is not worth it yet.
    const timer = window.setInterval(() => void refresh(), 1500);
    return () => window.clearInterval(timer);
  }, [refresh]);

  const enqueue = async () => {
    setError(null);
    try {
      await api.enqueueEcho(message, 5);
      await refresh();
    } catch (caught) {
      setError(caught);
    }
  };

  return (
    <div className="stack">
      <h2>Jobs</h2>
      <p className="muted">
        The echo handler is the only one for now. It exists to prove the queue end to end;
        solving and rendering arrive in later milestones.
      </p>
      <ErrorNotice error={error} />

      <div className="card row">
        <input
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          placeholder="Message to echo"
          aria-label="Message to echo"
        />
        <button className="primary" onClick={enqueue}>
          Enqueue echo job
        </button>
      </div>

      {jobs.length === 0 ? (
        <Empty>No jobs yet.</Empty>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Kind</th>
              <th>Status</th>
              <th>Progress</th>
              <th>Attempts</th>
              <th>Result</th>
            </tr>
          </thead>
          <tbody>
            {jobs.map((job) => (
              <tr key={job.id}>
                <td className="mono">{job.kind}</td>
                <td>{job.status}</td>
                <td style={{ minWidth: "10rem" }}>
                  <ProgressBar value={job.progress} />
                  <span className="muted">{job.progress_message}</span>
                </td>
                <td>
                  {job.attempts} / {job.max_attempts}
                </td>
                <td className="mono muted">
                  {job.error ? "failed" : job.result ? JSON.stringify(job.result) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// --- Settings ------------------------------------------------------------------

/**
 * How this person likes to draw: grid spacing, snapping and whether the grid
 * shows. Kept in this browser, applied to every design, never saved to one:
 * the grid is presentation (invariant 4).
 */
export function SettingsPage() {
  const [grid, setGrid] = useState(loadGrid);
  const [spacingText, setSpacingText] = useState(String(grid.spacing));
  const update = (change: Partial<typeof grid>) => {
    const next = { ...grid, ...change };
    setGrid(next);
    saveGrid(next);
  };
  const spacing = Number(spacingText);
  const spacingValid =
    Number.isInteger(spacing) && spacing >= GRID_SPACING.minimum && spacing <= GRID_SPACING.maximum;
  return (
    <div className="stack">
      <h2>Settings</h2>
      <AccountSettings />
      <section className="card stack" aria-label="Drawing">
        <h3 style={{ margin: 0 }}>Drawing</h3>
        <label className="row">
          <input type="checkbox" checked={grid.snap} onChange={(event) => update({ snap: event.target.checked })} />
          Snap to the grid
        </label>
        <label className="row">
          <input
            type="checkbox"
            checked={grid.visible}
            onChange={(event) => update({ visible: event.target.checked })}
          />
          Show the grid
        </label>
        <label className="row">
          Grid spacing
          <input
            type="number"
            min={GRID_SPACING.minimum}
            max={GRID_SPACING.maximum}
            step={1}
            value={spacingText}
            aria-invalid={!spacingValid}
            aria-describedby="grid-spacing-help"
            style={{ width: "5rem" }}
            onChange={(event) => {
              setSpacingText(event.target.value);
              const value = Number(event.target.value);
              if (Number.isInteger(value) && value >= GRID_SPACING.minimum && value <= GRID_SPACING.maximum) {
                update({ spacing: value });
              }
            }}
          />
        </label>
        <p id="grid-spacing-help" className="muted" style={{ margin: 0 }}>
          {spacingValid
            ? `Between ${GRID_SPACING.minimum} and ${GRID_SPACING.maximum}. The library's symbols are drawn to a 16 grid, so 16 or 8 keeps ports on grid lines.`
            : `Enter a whole number between ${GRID_SPACING.minimum} and ${GRID_SPACING.maximum}.`}
        </p>
        <p className="muted" style={{ margin: 0 }}>
          Kept in this browser and used for every design. Takes effect when a design is next opened.
        </p>
      </section>
    </div>
  );
}
